import React from "react";

import { activeProduct } from "@/lib/productBrand";

/**
 * Top-level error boundary — the last line of defence against a blank
 * white screen. Without this, any uncaught render error anywhere in the
 * tree unmounts the entire app with zero indication to the user of what
 * happened and no way to recover short of guessing to hit refresh.
 *
 * Deliberately minimal and dependency-free (no Tailwind utility classes,
 * no other app components) — the fallback itself has to be bulletproof,
 * since there's nothing left to catch an error thrown while rendering it.
 *
 * The one exception is the support address, and it is worth the risk: this
 * screen was giving American agents a British email on a British domain, at
 * the exact moment the product had just broken in front of them. Read behind
 * a try/catch, with the address dropped entirely rather than guessed at if
 * the lookup throws — the reload button is the part that matters.
 */
function supportEmail() {
  try {
    return activeProduct().supportEmail || "";
  } catch {
    return "";
  }
}
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("Unhandled error caught by ErrorBoundary:", error, info?.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily: "Georgia, 'Cormorant Garamond', serif",
          background: "#f9f5ee",
          color: "#1a2e22",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase", color: "#a0653c", marginBottom: 16 }}>
            Something went wrong
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 500, marginBottom: 16, lineHeight: 1.2 }}>
            This page hit a snag.
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: "#4a4a4a", marginBottom: 28, fontFamily: "system-ui, sans-serif" }}>
            Reloading usually fixes it.{" "}
            {supportEmail() ? (
              <>
                If it keeps happening, email us at{" "}
                <a href={`mailto:${supportEmail()}`} style={{ color: "#a0653c" }}>
                  {supportEmail()}
                </a>{" "}
                and we'll get on it.
              </>
            ) : (
              "If it keeps happening, get in touch and we'll get on it."
            )}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: "#1a2e22",
              color: "#f9f5ee",
              border: "none",
              padding: "12px 28px",
              fontSize: 13,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              cursor: "pointer",
              fontFamily: "system-ui, sans-serif",
            }}
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
